/* eslint-disable no-console */
import { axiosClient } from '../../api';

const state = {
  giftcards: [],
  products: [],
  product: null,
  orders: [],
  notifications: [],
  eventsCodes: [],
  lastOrder: null,
  videos: [],
  accessories: [],
  quiz: null,
  checklists: [],
  checkedListItems: [],
  trainings: [],
  allTrainings: [],
  referralStats: {},
  transactions: {},
  languages: [],
  language: {},
};

const getters = {};

const actions = {
  // ---------------- queries ------------------
  async getGiftcards({ commit }) {
    try {
      const res = await axiosClient.get('giftcards');
      commit('setGiftcards', res.data);
    } catch (error) {
      console.error({ ...error });
    }
  },
  async getProducts({ commit }) {
    try {
      const res = await axiosClient.get('products');
      commit('setProducts', res.data.data);
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getProduct(
    { commit },
    {
      productId,
      includeVideos = false,
      includeResources = false,
      includeAccessories = false,
      lang_code = 'en',
    },
  ) {
    let params = {};
    params.includes = [];
    if (includeVideos) {
      params.includes.push('post_training_videos');
      params.includes.push('training_videos');
      params.includes.push('pretraining');
    }
    if (includeAccessories) {
      params.includes.push('accessories');
    }
    if (includeResources) {
      params.includes.push('resources');
      params.includes.push('post_training_resources');
    }
    params.lang_code = lang_code;
    try {
      const res = await axiosClient.get(`products/${productId}`, { params });
      commit('setProduct', res.data.data);
      return res.data.data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getOrders({ commit }) {
    try {
      const res = await axiosClient.get('orders', {
        params: {
          include: ['fulfillments', 'fulfillments.statuses', 'fulfillments.items'],
        },
      });
      commit('setOrders', res.data.data);
      return res.data.data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getNotifications({ commit }) {
    try {
      const res = await axiosClient.get('notifications');
      commit('setNotifications', res.data.data);
      return res.data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getEventsCodes({ commit }) {
    try {
      const res = await axiosClient.get('events');
      commit('setEventsCodes', res.data.data);
      return res.data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getLastOrder({ commit }) {
    try {
      const res = await axiosClient.get('orders', {
        params: {
          include: ['fulfillments', 'fulfillments.statuses', 'fulfillments.items'],
          limit: 1,
          order_by: 'created_at',
          order_dir: 'desc',
        },
      });
      if (res.data.data && res.data.data.length) {
        commit('setLastOrder', res.data.data[0]);
      }
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getAccessories({ commit }) {
    try {
      const res = await axiosClient.get('/accessories');
      commit('setAccessories', res.data.data);
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getCheckLists({ commit }, { videoId, langCode }) {
    try {
      const res = await axiosClient.get(`videos/${videoId}/checklists?lang_code=${langCode}`);
      const checklists = res.data.data;
      commit('setCheckLists', { checklists });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getCheckedListsItems({ commit }, { videoId }) {
    try {
      const res = await axiosClient.get(`videos/${videoId}/checklists-customer`);
      const checklists = res.data;
      commit('setCheckedListItems', { checklists });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async addCheckedListsItems({ commit }, { checklist_id }) {
    commit('addCheckedListItems', { checklist_id });
  },

  async getQuiz({ commit }, { videoId, langCode }) {
    try {
      const res = await axiosClient.get(`videos/${videoId}/quiz?lang_code=${langCode}`);
      const quiz = res.data.data;
      commit('setQuiz', { quiz });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getTrainings({ commit }) {
    try {
      const { data } = await axiosClient.get('training/appointments');
      const trainings = data.data
        .filter(({ cancelled }) => !cancelled)
        .map(({ id, product_group, time_slot }) => ({
          id,
          machine: product_group.name,
          trainingId: time_slot.id || '-',
          trainingTime: time_slot.time || '-',
          trainingType: time_slot.type_name || '-',
          trainingTopic: time_slot.training_topic || '-',
          classLink: time_slot.class_link,
          classNote: time_slot.class_note,
        }));
      commit('setTrainings', { trainings });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getAllTrainings({ commit }) {
    try {
      const { data } = await axiosClient.get('training/all_appointments');
      const trainings = data.data.map(({ id, product_group, time_slot }) => ({
        id,
        machine: product_group.name,
        trainingId: time_slot.id || '-',
        trainingTime: time_slot.time || '-',
        trainingType: time_slot.type_name || '-',
        trainingTopic: time_slot.training_topic || '-',
        classLink: time_slot.class_link,
        classNote: time_slot.class_note,
        videoRecordUrl: time_slot.video_record_url,
        videoRecordUrlComplete: time_slot.video_record_url_complete,
      }));
      commit('setAllTrainings', { trainings });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getReferralStats({ commit }, params) {
    try {
      const { data } = await axiosClient.get('customer/get_referral_stats', { params });

      const {
        referred_order_items,
        referred_customers_count,
        redeemed_amount,
        unredeemed_amount,
        available_credit,
      } = data.data;

      commit('setReferralsStats', {
        referredOrderItems: referred_order_items,
        referredCustomersCount: referred_customers_count,
        redeemedAmount: redeemed_amount,
        unredeemedAmount: unredeemed_amount,
        availableCredit: available_credit,
      });

      return referred_order_items;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async getTransactions({ commit }, params) {
    try {
      const { data } = await axiosClient.get('/transaction', { params });
      commit('setTransactions', data);
      return data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  // ---------------- mutations ------------------
  async saveVideoProgress({ commit }, { videoId, time, complete }) {
    try {
      const res = await axiosClient.post(`videos/${videoId}`, { time, complete });
      commit('updateVideos', { video: res.data.data.data });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async submitQuiz(_, { quizId, answers }) {
    try {
      const res = await axiosClient.post(`quiz/${quizId}/answer`, { answers });
      return res.data;
    } catch (error) {
      console.error({ ...error });
    }
  },

  async scheduleTraining({ commit }, { slotId, groupId, phone }) {
    try {
      const { data } = await axiosClient.post('training/book', {
        slot_id: slotId,
        group_id: groupId,
        phone,
      });

      const { id, product_group, time_slot } = data.data;
      const training = {
        id,
        machine: product_group.name,
        trainingId: time_slot.id,
        trainingTime: time_slot.time,
        trainingType: time_slot.type_name,
        trainingTopic: time_slot.training_topic,
        classNote: time_slot.class_note,
        classLink: time_slot.class_link,
      };

      commit('addTraining', { training });
    } catch (error) {
      console.error({ ...error });
      throw error;
    }
  },

  async cancelTraining({ commit }, { trainingId }) {
    try {
      await axiosClient.delete(`training/appointments/${trainingId}/cancel`);
      commit('removeTraining', { trainingId });
    } catch (error) {
      console.error({ ...error });
    }
  },

  async removeNotifications({ commit }, ids) {
    try {
      await axiosClient.patch('notifications/read', { ids });
      commit('removeNotifications', { ids });
    } catch (error) {
      console.error({ ...error });
    }
  },
};

const mutations = {
  setGiftcards(state, giftcards) {
    console.log(giftcards);
    state.giftcards = giftcards;
  },
  setProducts(state, products) {
    state.products = products;
  },
  setProduct(state, product) {
    state.product = product;
  },
  setOrders(state, orders) {
    state.orders = orders;
  },
  setNotifications(state, notifications) {
    state.notifications = notifications;
  },
  setEventsCodes(state, eventsCodes) {
    state.eventsCodes = eventsCodes;
  },
  setLastOrder(state, order) {
    state.lastOrder = order;
  },
  setVideos(state, { videos }) {
    state.videos = videos;
  },
  setAccessories(state, accessories) {
    state.accessories = accessories;
  },
  updateVideos(state, { video }) {
    state.videos = state.videos.map(v => (v.id === video.id ? video : v));
  },
  setCheckLists(state, { checklists }) {
    state.checklists = checklists;
  },
  setCheckedListItems(state, { checklists }) {
    state.checkedListItems = checklists;
  },
  addCheckedListItems(state, { checklist_id }) {
    state.checkedListItems.push(checklist_id);
  },
  setQuiz(state, { quiz }) {
    state.quiz = quiz;
  },
  setTrainings(state, { trainings }) {
    state.trainings = trainings;
  },
  setAllTrainings(state, { trainings }) {
    state.allTrainings = trainings;
  },
  addTraining(state, { training }) {
    state.trainings = [...state.trainings, training];
  },
  removeTraining(state, { trainingId }) {
    state.trainings = state.trainings.filter(s => s.id !== trainingId);
  },
  setReferralsStats(state, { referredOrderItems, ...rest }) {
    const { data, ...meta } = referredOrderItems;
    state.referralStats = { data, meta, ...rest };
  },
  setTransactions(state, { data, meta }) {
    state.transactions = { data, meta };
  },
  removeNotifications(state, { ids }) {
    state.notifications = state.notifications.filter(n => !ids.some(id => id === n.id));
  },
};

export const customer = {
  actions,
  getters,
  state,
  mutations,
  namespaced: true,
};
